import { FieldArray, FieldArrayRenderProps, isNaN, useField } from 'formik';
import { debounce } from 'lodash';
import React, { useCallback, useContext, useRef } from 'react';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { ReactComponent as DeleteIcon } from '../../../../../images/icons/delete.svg';
import { ButtonIcon, SecondaryButton } from '../../../../../shared/Button';
import { TextInputField } from '../../../../../shared/form/inputs/TextInputField';
import { Header3 } from '../../../../../shared/Headers';
import { Table, TBody, Td, Th, THead, Tr } from '../../../../../shared/Table';
import { spacing04, spacing06 } from '../../../../../styling/spacing';
import { DesignFormDefaultsContext } from '../../../../designs/DesignFormDefaultsContext';
import { QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import { getNewRoomRoofHeatLossDetailsFormModel } from '../../rooms/heatLoss/roomRoofHeatLossDetailsFormModel';
import { UnitFormModel } from '../unitFormModel';
import { onUnitRoofTemperatureChange } from './unitElementOnChangeHelpers';
import { getUnitDefaultExternalAirTemperature } from './unitHeatLossDetailsFormModel';
import { getDefaultUnitRoofElementFormModel } from './unitRoofElementFormModel';

type Props = {
  unit: UnitFormModel;
  unitIndex: number;
};

export const UnitRoofElementsField = ({ unit, unitIndex }: Props) => {
  const roofElementsFieldName = `units.${unitIndex}.heatLossDetails.roofElements`;

  const [externalAirTemperatureField] = useField<string>(getUnitDefaultExternalAirTemperature(unitIndex));
  const { unitRoofElements, setUnitRoofElements } = useContext(DesignFormDefaultsContext);

  const roofElementFieldName = (roofElementIndex: number, fieldName: string) =>
    `${roofElementsFieldName}.${roofElementIndex}.${fieldName}`;

  const { heatLossDetails } = unit;

  const { externalAirTemperatureInDegreesCelsius: projectExternalAirTemperature } = assertNotNull(
    useContext(QuoteOrDesignFormContext),
  );

  const externalAirTemperatureInDegreesCelsius = isNaN(Number(externalAirTemperatureField.value))
    ? projectExternalAirTemperature
    : Number(externalAirTemperatureField.value);

  const newRoofElementNumber = useRef(heatLossDetails == null ? 1 : heatLossDetails.roofElements.length + 1);

  const addRoofElement = (fieldArrayRenderProps: FieldArrayRenderProps) => {
    const newRoofElement = {
      ...getDefaultUnitRoofElementFormModel(externalAirTemperatureInDegreesCelsius.toString()),
      name: `Roof Element ${newRoofElementNumber.current}`,
    };

    newRoofElementNumber.current++;

    fieldArrayRenderProps.push(newRoofElement);

    const newRoofManifoldTemperatures: Array<Array<string>> = [];
    unit.manifolds.forEach((manifold, manifoldIndex) => {
      const rooms = manifold.rooms || [];
      const newRoofRoomTemperatures: Array<string> = [];

      rooms.forEach((room, roomIndex) => {
        const roomHeatLossDetails = assertNotNull(room.heatLossDetails);

        const { roofs } = roomHeatLossDetails;

        const ceilingHeightInMetresOrNull = isNaN(Number(roomHeatLossDetails.ceilingHeightInMetres))
          ? null
          : Number(roomHeatLossDetails.ceilingHeightInMetres);

        const newRoomRoofDetailsForm = getNewRoomRoofHeatLossDetailsFormModel(
          ceilingHeightInMetresOrNull,
          room.floorLevelCode,
          externalAirTemperatureInDegreesCelsius,
          unit,
        );

        fieldArrayRenderProps.form.setFieldValue(
          `units.${unitIndex}.manifolds.${manifoldIndex}.rooms.${roomIndex}.heatLossDetails.roofs`,
          [...roofs, newRoomRoofDetailsForm],
        );

        newRoofRoomTemperatures.push(newRoomRoofDetailsForm.temperatureAboveInDegreesCelsius);
      });

      newRoofManifoldTemperatures.push(newRoofRoomTemperatures);
    });

    const newUnitRoofElement = {
      value: newRoofElement.defaultTemperatureAboveInDegreesCelsius,
      manifoldRoomValues: newRoofManifoldTemperatures,
    };

    setUnitRoofElements([...unitRoofElements, newUnitRoofElement]);
  };

  const removeRoofElement = (roofElementIndex: number, fieldArrayRenderProps: FieldArrayRenderProps) => {
    fieldArrayRenderProps.remove(roofElementIndex);

    unit.manifolds.forEach((manifold, manifoldIndex) => {
      const rooms = manifold.rooms || [];

      rooms.forEach((room, roomIndex) => {
        const roomHeatLossDetails = assertNotNull(room.heatLossDetails);

        const { roofs } = roomHeatLossDetails;

        fieldArrayRenderProps.form.setFieldValue(
          `units.${unitIndex}.manifolds.${manifoldIndex}.rooms.${roomIndex}.heatLossDetails.roofs`,
          roofs.filter((_, roofIndex) => roofIndex !== roofElementIndex),
        );
      });
    });

    setUnitRoofElements(unitRoofElements.filter((_, index) => index !== roofElementIndex));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedTemperatureChange = useCallback(
    debounce((newValue: string, roofIndex: number, fieldArrayRenderProps: FieldArrayRenderProps) => {
      onUnitRoofTemperatureChange(
        newValue,
        unitIndex,
        roofIndex,
        fieldArrayRenderProps.form,
        unitRoofElements,
        setUnitRoofElements,
      );
    }, 300),
    [unitRoofElements, setUnitRoofElements, unitIndex],
  );

  const handleTemperatureChange = (
    newValue: string,
    roofIndex: number,
    fieldArrayRenderProps: FieldArrayRenderProps,
  ) => {
    debouncedTemperatureChange(newValue, roofIndex, fieldArrayRenderProps);
  };

  if (heatLossDetails == null) {
    return null;
  }

  return (
    <FieldArray name={`units.${unitIndex}.heatLossDetails.roofElements`}>
      {fieldArrayRenderProps => (
        <Container>
          <Header3>Roof Elements</Header3>
          <Table>
            <THead>
              <Tr>
                <Th>Name</Th>
                <Th>Roof U-Value</Th>
                <Th>Skylight U-Value</Th>
                <Th>Default Temp Above (°C)</Th>
                <DeleteButtonTableHeader />
              </Tr>
            </THead>
            <TBody>
              {heatLossDetails.roofElements.map((_, index) => (
                <Tr key={index}>
                  <Td>
                    <TextInputField name={roofElementFieldName(index, 'name')} label={null} />
                  </Td>
                  <Td>
                    <TextInputField name={roofElementFieldName(index, 'roofThermalTransmittance')} label={null} />
                  </Td>
                  <Td>
                    <TextInputField name={roofElementFieldName(index, 'skylightThermalTransmittance')} label={null} />
                  </Td>
                  <Td>
                    <TextInputField
                      name={roofElementFieldName(index, 'defaultTemperatureAboveInDegreesCelsius')}
                      label={null}
                      onChange={newValue => {
                        handleTemperatureChange(newValue, index, fieldArrayRenderProps);
                      }}
                    />
                  </Td>
                  <Td>
                    {heatLossDetails.roofElements.length > 1 && (
                      <SecondaryButton
                        onClick={() => removeRoofElement(index, fieldArrayRenderProps)}
                        data-testid={deleteRoofElementButtonTestId(index)}
                      >
                        <ButtonIcon>
                          <DeleteIcon />
                        </ButtonIcon>
                      </SecondaryButton>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
          <AddRoofElementButton onClick={() => addRoofElement(fieldArrayRenderProps)}>
            + Add Roof Element
          </AddRoofElementButton>
        </Container>
      )}
    </FieldArray>
  );
};

export const deleteRoofElementButtonTestId = (roofElementIndex: number) =>
  `delete-roof-element-button-${roofElementIndex}`;

const Container = styled.div`
  margin-bottom: ${spacing06};
`;

const DeleteButtonTableHeader = styled(Th)`
  width: 65px;
`;

const AddRoofElementButton = styled(SecondaryButton)`
  margin-top: ${spacing04};
`;
